import { useState } from 'react'
import { notifications } from '@mantine/notifications'
import {
  type PlanoCreateInput,
  type PlanoImportResultado,
  downloadTemplatePlanos,
  usePlanoCreate,
  usePlanoDelete,
  usePlanoImportar,
  usePlanoList,
} from '../../hooks/usePmoc'
import AplicarPlanosNbrModal from './AplicarPlanosNbrModal'
import { extractApiError } from '../../lib/extractApiError'
import type { PMOC } from '../../types'

const TIPOS_OS: Array<{ value: string; label: string }> = [
  { value: 'preventivo', label: 'Preventivo' },
  { value: 'corretivo', label: 'Corretivo' },
  { value: 'higienizacao', label: 'Higienização' },
  { value: 'qai', label: 'Análise QAI' },
  { value: 'desinfeccao_legionella', label: 'Desinfecção — Legionella' },
]

const PERIODICIDADES: Array<{ value: string; label: string }> = [
  { value: 'diario', label: 'Diário' },
  { value: 'semanal', label: 'Semanal' },
  { value: 'quinzenal', label: 'Quinzenal' },
  { value: 'mensal', label: 'Mensal' },
  { value: 'trimestral', label: 'Trimestral' },
  { value: 'semestral', label: 'Semestral' },
  { value: 'anual', label: 'Anual' },
]

const TIPO_LABEL = Object.fromEntries(TIPOS_OS.map((t) => [t.value, t.label]))
const PERIOD_LABEL = Object.fromEntries(PERIODICIDADES.map((p) => [p.value, p.label]))

interface Props {
  pmoc: PMOC
}

export default function PlanosSection({ pmoc }: Props) {
  const podeEditar = pmoc.status === 'rascunho'
  const lista = usePlanoList(pmoc.id)
  const create = usePlanoCreate(pmoc.id)
  const remove = usePlanoDelete(pmoc.id)
  const importar = usePlanoImportar(pmoc.id)
  const [aberto, setAberto] = useState(false)
  const [nbrAberto, setNbrAberto] = useState(false)
  const [importarAberto, setImportarAberto] = useState(false)
  const [arquivoImport, setArquivoImport] = useState<File | null>(null)
  const [previewImport, setPreviewImport] = useState<PlanoImportResultado | null>(null)
  const [form, setForm] = useState<PlanoCreateInput>({
    equipamento: null,
    tipo_os: 'preventivo',
    descricao: '',
    periodicidade: 'mensal',
  })

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    if (!form.descricao.trim()) {
      notifications.show({ color: 'red', message: 'Descrição obrigatória.' })
      return
    }
    try {
      await create.mutateAsync(form)
      notifications.show({ color: 'green', message: 'Plano de manutenção criado.' })
      setForm({
        equipamento: null,
        tipo_os: 'preventivo',
        descricao: '',
        periodicidade: 'mensal',
      })
      setAberto(false)
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Falha',
        message: extractApiError(err, 'Erro ao salvar plano.'),
      })
    }
  }

  async function handlePreviewImport() {
    if (!arquivoImport) return
    try {
      const r = await importar.mutateAsync({ arquivo: arquivoImport, dry_run: true })
      setPreviewImport(r)
    } catch (err) {
      const msg = (err as { response?: { data?: { arquivo?: string } } })?.response?.data?.arquivo
      notifications.show({
        color: 'red',
        title: 'Falha no preview',
        message: msg ?? 'Erro ao analisar planilha.',
      })
    }
  }

  async function handleConfirmarImport() {
    if (!arquivoImport || !previewImport || previewImport.a_criar === 0) return
    if (!confirm(`Importar ${previewImport.a_criar} plano(s) ao PMOC?`)) return
    try {
      const r = await importar.mutateAsync({ arquivo: arquivoImport, dry_run: false })
      notifications.show({
        color: 'green',
        message: `${r.commit?.criados ?? 0} plano(s) criado(s).`,
      })
      setImportarAberto(false)
      setArquivoImport(null)
      setPreviewImport(null)
    } catch {
      notifications.show({ color: 'red', message: 'Falha no commit.' })
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Remover este plano? (soft-delete — pode reativar via admin)')) return
    try {
      await remove.mutateAsync(id)
      notifications.show({ color: 'green', message: 'Plano removido.' })
    } catch {
      notifications.show({ color: 'red', message: 'Falha ao remover.' })
    }
  }

  return (
    <section
      style={{
        background: 'white',
        padding: '1.5rem',
        borderRadius: 8,
        marginBottom: '1rem',
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
      }}
      data-testid="planos-section"
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '0.5rem',
        }}
      >
        <h2 style={{ margin: 0, fontSize: '1rem' }}>
          Planos de manutenção ({lista.data?.results.length ?? 0})
        </h2>
        {podeEditar && (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              type="button"
              onClick={() => downloadTemplatePlanos()}
              style={{
                padding: '0.4rem 0.8rem',
                background: 'white',
                color: '#1565C0',
                border: '1px solid #1565C0',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: '0.85rem',
              }}
            >
              📥 Template
            </button>
            <button
              type="button"
              onClick={() => {
                setImportarAberto((v) => !v)
                setAberto(false)
              }}
              data-testid="plano-importar-toggle"
              style={{
                padding: '0.4rem 0.8rem',
                background: importarAberto ? '#888' : '#00897B',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: '0.85rem',
              }}
            >
              {importarAberto ? '× Cancelar' : '📊 Importar xlsx'}
            </button>
            <button
              type="button"
              onClick={() => setNbrAberto(true)}
              data-testid="plano-aplicar-nbr"
              style={{
                padding: '0.4rem 0.8rem',
                background: '#00897B',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: 600,
              }}
            >
              📋 Aplicar planos NBR
            </button>
            <button
              type="button"
              onClick={() => setAberto((v) => !v)}
              data-testid="plano-toggle-form"
              style={{
                padding: '0.4rem 0.8rem',
                background: aberto ? '#888' : '#1565C0',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: '0.85rem',
              }}
            >
              {aberto ? '× Cancelar' : '+ Novo plano'}
            </button>
          </div>
        )}
      </div>

      <AplicarPlanosNbrModal
        open={nbrAberto}
        onClose={() => setNbrAberto(false)}
        pmoc={pmoc}
      />

      {importarAberto && podeEditar && (
        <div
          style={{
            padding: '0.75rem',
            background: '#FAFAFA',
            borderRadius: 4,
            marginBottom: '1rem',
          }}
        >
          <p style={{ margin: '0 0 0.5rem', fontSize: '0.85rem', color: '#666' }}>
            Suba xlsx com colunas TIPO_OS, PERIODICIDADE, DESCRIÇÃO (obrigatórias) e
            EQUIPAMENTO_SERIE (opcional). Baixe o template no botão acima.
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <input
              type="file"
              accept=".xlsx,.xls"
              data-testid="plano-importar-arquivo"
              onChange={(e) => {
                setArquivoImport(e.target.files?.[0] ?? null)
                setPreviewImport(null)
              }}
            />
            <button
              type="button"
              onClick={handlePreviewImport}
              disabled={!arquivoImport || importar.isPending}
              style={{
                padding: '0.4rem 0.8rem',
                background: '#1565C0',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: '0.85rem',
              }}
            >
              {importar.isPending ? '...' : 'Analisar'}
            </button>
          </div>
          {previewImport && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <span>{previewImport.total_linhas} lidas</span>
                <span style={{ color: '#2E7D32' }}>{previewImport.a_criar} a criar</span>
                <span style={{ color: '#F9A825' }}>{previewImport.duplicados} dup.</span>
                <span style={{ color: '#C62828' }}>{previewImport.erros} erros</span>
              </div>
              <button
                type="button"
                onClick={handleConfirmarImport}
                disabled={previewImport.a_criar === 0 || importar.isPending}
                style={{
                  marginTop: '0.5rem',
                  padding: '0.4rem 1rem',
                  background: '#2E7D32',
                  color: 'white',
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                }}
              >
                ✓ Confirmar ({previewImport.a_criar})
              </button>
            </div>
          )}
        </div>
      )}

      {!podeEditar && (
        <p style={{ color: '#999', fontSize: '0.85rem', marginTop: 0 }}>
          PMOC em status <strong>{pmoc.status}</strong> — planos não editáveis. Duplique
          o PMOC para criar uma nova versão rascunho.
        </p>
      )}

      {aberto && podeEditar && (
        <form
          onSubmit={handleCreate}
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 2fr 1fr auto',
            gap: '0.5rem',
            padding: '0.75rem',
            background: '#FAFAFA',
            borderRadius: 4,
            marginBottom: '1rem',
            alignItems: 'end',
          }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.8rem' }}>
            Tipo de OS *
            <select
              data-testid="plano-tipo-os"
              value={form.tipo_os}
              onChange={(e) => setForm({ ...form, tipo_os: e.target.value })}
              style={{ padding: '0.4rem' }}
            >
              {TIPOS_OS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.8rem' }}>
            Periodicidade *
            <select
              data-testid="plano-periodicidade"
              value={form.periodicidade}
              onChange={(e) => setForm({ ...form, periodicidade: e.target.value })}
              style={{ padding: '0.4rem' }}
            >
              {PERIODICIDADES.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.8rem' }}>
            Descrição *
            <input
              data-testid="plano-descricao"
              value={form.descricao}
              onChange={(e) => setForm({ ...form, descricao: e.target.value })}
              required
              placeholder="ex.: Limpeza de filtros + verificação carga"
              style={{ padding: '0.4rem' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.8rem' }}>
            Equipamento
            <select
              data-testid="plano-equipamento"
              value={form.equipamento ?? ''}
              onChange={(e) =>
                setForm({ ...form, equipamento: e.target.value || null })
              }
              style={{ padding: '0.4rem' }}
            >
              <option value="">— todos do PMOC —</option>
              {pmoc.equipamentos?.map((eq) => (
                <option key={eq.id} value={eq.id}>
                  {eq.marca} {eq.modelo}
                </option>
              ))}
            </select>
          </label>
          <button
            type="submit"
            disabled={create.isPending}
            data-testid="plano-submit"
            style={{
              padding: '0.4rem 1rem',
              background: '#2E7D32',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontWeight: 600,
              height: 32,
            }}
          >
            {create.isPending ? '...' : 'Criar'}
          </button>
        </form>
      )}

      {lista.isLoading ? (
        <p style={{ color: '#666', margin: 0 }}>Carregando planos...</p>
      ) : (lista.data?.results.length ?? 0) === 0 ? (
        <p style={{ color: '#666', margin: 0 }}>
          Nenhum plano cadastrado. Adicione planos para gerar OS automaticamente.
        </p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead>
            <tr style={{ background: '#F5F5F5' }}>
              <th style={{ padding: '0.5rem', textAlign: 'left' }}>Tipo</th>
              <th style={{ padding: '0.5rem', textAlign: 'left' }}>Periodicidade</th>
              <th style={{ padding: '0.5rem', textAlign: 'left' }}>Descrição</th>
              <th style={{ padding: '0.5rem', textAlign: 'left' }}>Equipamento</th>
              <th style={{ padding: '0.5rem', textAlign: 'left' }}>Próxima</th>
              {podeEditar && <th style={{ padding: '0.5rem' }}></th>}
            </tr>
          </thead>
          <tbody>
            {lista.data?.results.map((p) => (
              <tr key={p.id} style={{ borderTop: '1px solid #EEE' }}>
                <td style={{ padding: '0.5rem' }}>
                  {TIPO_LABEL[p.tipo_os] ?? p.tipo_os}
                </td>
                <td style={{ padding: '0.5rem' }}>
                  {PERIOD_LABEL[p.periodicidade] ?? p.periodicidade}
                </td>
                <td style={{ padding: '0.5rem' }}>{p.descricao}</td>
                <td style={{ padding: '0.5rem' }}>
                  {p.equipamento_str ?? (p.equipamento ? '(equipamento)' : 'todos')}
                </td>
                <td style={{ padding: '0.5rem', color: '#666' }}>
                  {p.proxima_execucao ?? '—'}
                </td>
                {podeEditar && (
                  <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                    <button
                      type="button"
                      onClick={() => handleDelete(p.id)}
                      disabled={remove.isPending}
                      style={{
                        padding: '0.2rem 0.5rem',
                        background: 'white',
                        color: '#C62828',
                        border: '1px solid #C62828',
                        borderRadius: 4,
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                      }}
                    >
                      Remover
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}
